import React, { useEffect, useMemo, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import AddInCircleIcon from '@grnhse/seedling/lib/birch/icons/add_in_circle';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import { useUserContext } from 'talent_matching/contexts/user_context';
import type { FilterGroup } from 'talent_matching/types';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import SingleFilter from 'talent_matching/components/filters/single_filter';
import styled from 'styled-components';

const t = withLocalePrefix('talent_matching.filter_row.education_filters');

const InlineAction = styled.div`
  align-self: flex-start;
  display: inline-flex;
`;

type EducationFiltersSectionProps = {
  alwaysExpanded?: boolean;
};

const EducationFiltersSection = ({
  alwaysExpanded = false,
}: EducationFiltersSectionProps) => {
  const { educationFilterConfig } = useUserContext();
  const { setValue, watch } = useFormContext();
  const educationFilters =
    (watch('educationFilters') as AdvancedFilter[]) || [];
  const educationGroup = useMemo(
    () =>
      (educationFilterConfig.filterGroups || []).find(
        (group: FilterGroup) => group.value === 'educationDetails'
      ),
    [educationFilterConfig.filterGroups]
  );
  const educationOptions = educationGroup?.items || [];

  const hasConfiguredFilters = educationFilters.some((filter: AdvancedFilter) =>
    Boolean(filter.type || filter.operator || filter.value)
  );
  const [isExpanded, setIsExpanded] = useState(
    hasConfiguredFilters || alwaysExpanded
  );

  useEffect(() => {
    if (hasConfiguredFilters) {
      setIsExpanded(true);
    }
  }, [hasConfiguredFilters]);

  useEffect(() => {
    if (educationFilters.length === 0) {
      setIsExpanded(alwaysExpanded);
      if (alwaysExpanded) {
        setValue('educationFilters', [new AdvancedFilter()], {
          shouldDirty: true,
        });
      }
    }
  }, [educationFilters.length, alwaysExpanded, setValue]);

  const handleAddEducationFilters = () => {
    setIsExpanded(true);
    if (educationFilters.length === 0) {
      setValue('educationFilters', [new AdvancedFilter()], {
        shouldDirty: true,
      });
    }
  };

  const handleAddFilter = () => {
    setValue('educationFilters', [...educationFilters, new AdvancedFilter()], {
      shouldDirty: true,
    });
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = [...educationFilters];
    newFilters.splice(index, 1);

    if (newFilters.length === 0 && alwaysExpanded) {
      setValue('educationFilters', [new AdvancedFilter()], {
        shouldDirty: true,
      });
      return;
    }

    setValue('educationFilters', newFilters, { shouldDirty: true });
    if (newFilters.length === 0) {
      setIsExpanded(alwaysExpanded);
    }
  };

  const handleRemoveEducationFilters = () => {
    setValue('educationFilters', [], { shouldDirty: true });
    setIsExpanded(alwaysExpanded);
  };

  if (!isExpanded) {
    return (
      <InlineAction>
        <SecondaryButton
          onClick={handleAddEducationFilters}
          icon={AddInCircleIcon}
          size="md"
        >
          {t('add_education_filters')}
        </SecondaryButton>
      </InlineAction>
    );
  }

  return (
    <Stack spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
      <SecondaryBody medium>{t('title')}</SecondaryBody>
      <Stack spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
        {educationFilters.map((filter: AdvancedFilter, index: number) => (
          <SingleFilter
            key={filter.reactKey}
            filter={filter}
            onChange={(updatedFilter: AdvancedFilter) => {
              const newFilters = [...educationFilters];
              newFilters.splice(index, 1, updatedFilter);
              setValue('educationFilters', newFilters, { shouldDirty: true });
            }}
            onRemove={() => handleRemoveFilter(index)}
            typeOptions={educationOptions}
          />
        ))}
        <InlineAction>
          <TertiaryButton
            onClick={handleAddFilter}
            icon={AddInCircleIcon}
            size="md"
          >
            {t('add_filter')}
          </TertiaryButton>
        </InlineAction>
      </Stack>
      {!alwaysExpanded && (
        <InlineAction>
          <TertiaryButton onClick={handleRemoveEducationFilters} size="md">
            {t('remove_education_filters')}
          </TertiaryButton>
        </InlineAction>
      )}
    </Stack>
  );
};

export default EducationFiltersSection;
